// ─── YogaGame.js ──────────────────────────────────────────────────────────────
// Yoga-themed endless runner game.
//
// Controls:
//   SPACE / Tap  → Inhale (hold to float up, release to fall with gravity)
//
// Collectibles : 🪷 Lotus  (+10 pts, heals 1 life)
// Obstacles    : ⚡ Stress blocks (lose 1 life, invincibility frames follow)
// Levels       : Speed + obstacle density increase every 200 pts
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  Section,
  GameHeader,
  GameTitle,
  GameSubtitle,
  HudRow,
  HudPill,
  CanvasWrapper,
  ControlsHint,
  KeyBadge,
  HintText,
  OverlayBox,
  OverlayEmoji,
  OverlayTitle,
  OverlayBody,
  StartButton,
  LivesRow,
  InfoStrip,
  InfoChip,
} from "./YogaGame_styles";
import { COLORS } from "../../common/colors";

// ─── Google Fonts injection (Cinzel Decorative + EB Garamond) ─────────────────
const injectFonts = () => {
  if (document.getElementById("yoga-game-fonts")) return;
  const link = document.createElement("link");
  link.id   = "yoga-game-fonts";
  link.rel  = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700&family=EB+Garamond:ital,wght@0,400;0,600;1,400&display=swap";
  document.head.appendChild(link);
};

// ─── Constants ────────────────────────────────────────────────────────────────

const W          = 700;
const H          = 320;
const GROUND_Y   = H - 60;
const GRAVITY    = 0.38;
const LIFT_FORCE = -0.55;
const YOGI_X     = 90;
const YOGI_SIZE  = 44;
const OBSTACLE_W = 32;
const OBSTACLE_H_MIN = 28;
const OBSTACLE_H_MAX = 70;
const LOTUS_R    = 14;
const INITIAL_SPEED = 3.2;
const SPEED_INC     = 0.35;
const LEVEL_EVERY   = 200;
const MAX_LIVES     = 3;
const INV_FRAMES    = 90; // invincibility duration after hit

// Palette pulled straight from COLORS so the canvas matches the theme
const PAL = {
  sky:       COLORS.bgWarm,
  ground:    COLORS.bgCool,
  groundLine:COLORS.border,
  yogi:      COLORS.primary,
  yogiAura:  COLORS.primaryGlow,
  obstacle:  COLORS.accentDark,
  lotus:     COLORS.accent,
  lotusGlow: COLORS.accentGlow,
  cloud:     COLORS.surface92,
  particle:  COLORS.primaryLight,
  text:      COLORS.textDark,
};

// ─── Particle helper ─────────────────────────────────────────────────────────

const makeParticles = (x, y, color, count = 8) =>
  Array.from({ length: count }, () => ({
    x, y,
    vx: (Math.random() - 0.5) * 5,
    vy: (Math.random() - 0.5) * 5 - 2,
    alpha: 1,
    r: Math.random() * 5 + 2,
    color,
  }));

// ─── Cloud helper ────────────────────────────────────────────────────────────

const makeCloud = (x) => ({
  x,
  y: 30 + Math.random() * 70,
  w: 60 + Math.random() * 60,
  speed: 0.4 + Math.random() * 0.4,
});

// ─── Component ───────────────────────────────────────────────────────────────

const YogaGame = () => {
  const canvasRef   = useRef(null);
  const stateRef    = useRef(null); // mutable game state (avoids stale closures)
  const rafRef      = useRef(null);
  const keyHeldRef  = useRef(false);

  const [uiState, setUiState] = useState("idle"); // idle | playing | paused | dead
  const [score,   setScore]   = useState(0);
  const [hiScore, setHiScore] = useState(() => {
    try { return parseInt(localStorage.getItem("ys_yoga_game_hi") || "0", 10); }
    catch { return 0; }
  });
  const [level,  setLevel]   = useState(1);
  const [lives,  setLives]   = useState(MAX_LIVES);
  const [crashed, setCrashed] = useState(false);
  const [scoreKey, setScoreKey] = useState(0); // forces re-mount for animation

  // ── Init game state ─────────────────────────────────────────────────────────

  const initState = useCallback(() => ({
    yogi: {
      y:  GROUND_Y - YOGI_SIZE,
      vy: 0,
      angle: 0,
      invFrames: 0,
    },
    obstacles: [],
    lotuses:   [],
    particles: [],
    clouds: [makeCloud(100), makeCloud(300), makeCloud(550)],
    score:  0,
    level:  1,
    lives:  MAX_LIVES,
    speed:  INITIAL_SPEED,
    frame:  0,
    nextObstacle: 90,
    nextLotus:    160,
    running: true,
  }), []);

  // ── Draw helpers ────────────────────────────────────────────────────────────

  const drawRoundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y,     x + w, y + r);
    ctx.lineTo(x + w,     y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r,     y + h);
    ctx.quadraticCurveTo(x, y + h,     x, y + h - r);
    ctx.lineTo(x,         y + r);
    ctx.quadraticCurveTo(x, y,         x + r, y);
    ctx.closePath();
  };

  const drawYogi = (ctx, yogi, frame) => {
    const cx = YOGI_X + YOGI_SIZE / 2;
    const cy = yogi.y + YOGI_SIZE / 2;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(yogi.angle);

    // Aura glow when in air
    if (yogi.y < GROUND_Y - YOGI_SIZE - 2) {
      const grd = ctx.createRadialGradient(0, 0, 8, 0, 0, YOGI_SIZE * 0.8);
      grd.addColorStop(0, PAL.yogiAura);
      grd.addColorStop(1, "transparent");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(0, 0, YOGI_SIZE * 0.8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Blink effect during invincibility
    const visible = yogi.invFrames <= 0 || Math.floor(yogi.invFrames / 6) % 2 === 0;

    if (visible) {
      // Body – lotus pose silhouette
      ctx.fillStyle = PAL.yogi;
      ctx.beginPath();
      ctx.arc(0, -2, 11, 0, Math.PI * 2); // head
      ctx.fill();

      ctx.fillStyle = COLORS.primaryLight;
      ctx.beginPath();
      ctx.arc(0, 14, 14, 0, Math.PI); // torso arc (seated)
      ctx.fill();

      // Legs crossed
      ctx.strokeStyle = PAL.yogi;
      ctx.lineWidth   = 3;
      ctx.lineCap     = "round";
      ctx.beginPath(); ctx.moveTo(-12, 14); ctx.lineTo(12, 22);  ctx.stroke();
      ctx.beginPath(); ctx.moveTo(12, 14);  ctx.lineTo(-12, 22); ctx.stroke();

      // Arms raised (Anjali-ish)
      ctx.beginPath(); ctx.moveTo(-8, 6); ctx.lineTo(-18, -4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo( 8, 6); ctx.lineTo( 18, -4); ctx.stroke();

      // Third eye dot
      ctx.fillStyle = COLORS.accent;
      ctx.beginPath();
      ctx.arc(0, -5, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  };

  const drawObstacle = (ctx, obs) => {
    ctx.save();
    // Gradient fill
    const grd = ctx.createLinearGradient(obs.x, obs.y, obs.x + OBSTACLE_W, obs.y + obs.h);
    grd.addColorStop(0, COLORS.accentDark);
    grd.addColorStop(1, COLORS.primaryDark);
    ctx.fillStyle = grd;
    drawRoundRect(ctx, obs.x, obs.y, OBSTACLE_W, obs.h, 6);
    ctx.fill();

    // Stress icon
    ctx.font      = `${Math.min(obs.h - 8, 22)}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("⚡", obs.x + OBSTACLE_W / 2, obs.y + obs.h / 2);

    ctx.restore();
  };

  const drawLotus = (ctx, lot, frame) => {
    ctx.save();
    ctx.translate(lot.x, lot.y);
    // Glow
    const grd = ctx.createRadialGradient(0, 0, 2, 0, 0, LOTUS_R * 1.8);
    grd.addColorStop(0, PAL.lotusGlow);
    grd.addColorStop(1, "transparent");
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(0, 0, LOTUS_R * 1.8, 0, Math.PI * 2); ctx.fill();

    // Spinning lotus petals
    const petals = 6;
    const pRot   = (frame * 0.018) % (Math.PI * 2);
    for (let i = 0; i < petals; i++) {
      const a = pRot + (i / petals) * Math.PI * 2;
      ctx.save();
      ctx.rotate(a);
      ctx.fillStyle = PAL.lotus;
      ctx.globalAlpha = 0.85;
      ctx.beginPath();
      ctx.ellipse(0, -LOTUS_R * 0.9, 4, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.fillStyle = COLORS.accentWarm;
    ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(0, 0, 5, 0, Math.PI * 2); ctx.fill();

    ctx.restore();
  };

  const drawCloud = (ctx, cloud) => {
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle   = PAL.cloud;
    ctx.beginPath();
    ctx.arc(cloud.x,            cloud.y,      cloud.w * 0.22, 0, Math.PI * 2);
    ctx.arc(cloud.x + cloud.w * 0.25, cloud.y - 8, cloud.w * 0.28, 0, Math.PI * 2);
    ctx.arc(cloud.x + cloud.w * 0.55, cloud.y,      cloud.w * 0.22, 0, Math.PI * 2);
    ctx.arc(cloud.x + cloud.w * 0.25, cloud.y + 8,  cloud.w * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  // ── Main game loop ──────────────────────────────────────────────────────────

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const gs  = stateRef.current;
    if (!gs || !gs.running) return;

    gs.frame++;

    // ── Physics ──────────────────────────────────────────────────────────────
    const yogi = gs.yogi;
    if (keyHeldRef.current) {
      yogi.vy += LIFT_FORCE;
    }
    yogi.vy += GRAVITY;
    yogi.vy  = Math.max(-6, Math.min(yogi.vy, 8));
    yogi.y  += yogi.vy;

    // Ground clamp
    if (yogi.y >= GROUND_Y - YOGI_SIZE) {
      yogi.y  = GROUND_Y - YOGI_SIZE;
      yogi.vy = 0;
    }
    // Ceiling clamp
    if (yogi.y <= 0) { yogi.y = 0; yogi.vy = 0; }

    // Tilt angle based on velocity
    yogi.angle = Math.min(0.35, Math.max(-0.35, yogi.vy * 0.045));

    if (yogi.invFrames > 0) yogi.invFrames--;

    // ── Clouds ───────────────────────────────────────────────────────────────
    gs.clouds.forEach(c => { c.x -= c.speed; });
    gs.clouds = gs.clouds.filter(c => c.x + 120 > 0);
    if (gs.clouds.length < 4 && Math.random() < 0.005) {
      gs.clouds.push(makeCloud(W + 20));
    }

    // ── Obstacles ────────────────────────────────────────────────────────────
    gs.nextObstacle--;
    if (gs.nextObstacle <= 0) {
      const h = OBSTACLE_H_MIN + Math.random() * (OBSTACLE_H_MAX - OBSTACLE_H_MIN);
      gs.obstacles.push({ x: W + 10, y: GROUND_Y - h, h });
      const gap = Math.max(50, 110 - gs.level * 8);
      gs.nextObstacle = gap + Math.floor(Math.random() * 40);
    }
    gs.obstacles.forEach(o => { o.x -= gs.speed; });
    gs.obstacles = gs.obstacles.filter(o => o.x + OBSTACLE_W > -10);

    // ── Lotuses ──────────────────────────────────────────────────────────────
    gs.nextLotus--;
    if (gs.nextLotus <= 0) {
      gs.lotuses.push({
        x: W + 10,
        y: 60 + Math.random() * (GROUND_Y - 120),
      });
      gs.nextLotus = 180 + Math.floor(Math.random() * 80);
    }
    gs.lotuses.forEach(l => { l.x -= gs.speed * 0.85; });
    gs.lotuses = gs.lotuses.filter(l => l.x > -20);

    // ── Particles ────────────────────────────────────────────────────────────
    gs.particles.forEach(p => {
      p.x += p.vx; p.y += p.vy;
      p.vy += 0.15;
      p.alpha -= 0.03;
    });
    gs.particles = gs.particles.filter(p => p.alpha > 0);

    // ── Score & level ────────────────────────────────────────────────────────
    gs.score++;
    const newLevel = 1 + Math.floor(gs.score / LEVEL_EVERY);
    if (newLevel !== gs.level) {
      gs.level = newLevel;
      gs.speed = INITIAL_SPEED + (newLevel - 1) * SPEED_INC;
      setLevel(newLevel);
    }

    // ── Collisions ───────────────────────────────────────────────────────────
    const yogiCX = YOGI_X + YOGI_SIZE / 2;
    const yogiCY = yogi.y + YOGI_SIZE / 2;
    const hitR   = YOGI_SIZE * 0.38;

    // Obstacles
    if (yogi.invFrames <= 0) {
      for (const obs of gs.obstacles) {
        if (
          yogiCX + hitR > obs.x + 4 &&
          yogiCX - hitR < obs.x + OBSTACLE_W - 4 &&
          yogiCY + hitR > obs.y &&
          yogiCY - hitR < obs.y + obs.h
        ) {
          gs.lives--;
          yogi.invFrames = INV_FRAMES;
          gs.particles.push(...makeParticles(yogiCX, yogiCY, COLORS.accentDark, 12));
          setCrashed(true);
          setTimeout(() => setCrashed(false), 400);
          setLives(gs.lives);
          if (gs.lives <= 0) {
            gs.running = false;
            setUiState("dead");
            setScore(gs.score);
            setHiScore(prev => {
              const next = Math.max(prev, gs.score);
              try { localStorage.setItem("ys_yoga_game_hi", String(next)); } catch {}
              return next;
            });
          }
          break;
        }
      }
    }

    // Lotuses
    gs.lotuses = gs.lotuses.filter(lot => {
      const dx = yogiCX - lot.x;
      const dy = yogiCY - lot.y;
      if (Math.sqrt(dx * dx + dy * dy) < hitR + LOTUS_R) {
        gs.score += 10;
        if (gs.lives < MAX_LIVES) gs.lives++;
        gs.particles.push(...makeParticles(lot.x, lot.y, COLORS.accent, 8));
        setLives(gs.lives);
        setScoreKey(k => k + 1);
        return false;
      }
      return true;
    });

    setScore(gs.score);

    // ── Draw ─────────────────────────────────────────────────────────────────
    // Sky
    const skyGrd = ctx.createLinearGradient(0, 0, 0, H);
    skyGrd.addColorStop(0, COLORS.bgWarm);
    skyGrd.addColorStop(1, COLORS.bgCool);
    ctx.fillStyle = skyGrd;
    ctx.fillRect(0, 0, W, H);

    // Clouds
    gs.clouds.forEach(c => drawCloud(ctx, c));

    // Ground
    ctx.fillStyle = COLORS.bgCool;
    ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
    ctx.strokeStyle = COLORS.primary;
    ctx.lineWidth   = 2;
    ctx.globalAlpha = 0.25;
    ctx.beginPath(); ctx.moveTo(0, GROUND_Y); ctx.lineTo(W, GROUND_Y); ctx.stroke();
    ctx.globalAlpha = 1;

    // Ground ornament dots
    for (let gx = 0; gx < W; gx += 40) {
      ctx.fillStyle = COLORS.accentTint;
      ctx.beginPath();
      ctx.arc(gx + (gs.frame % 40), GROUND_Y + 10, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Lotuses
    gs.lotuses.forEach(l => drawLotus(ctx, l, gs.frame));

    // Obstacles
    gs.obstacles.forEach(o => drawObstacle(ctx, o));

    // Yogi
    drawYogi(ctx, yogi, gs.frame);

    // Particles
    gs.particles.forEach(p => {
      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle   = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });

    rafRef.current = requestAnimationFrame(loop);
  }, []);

  // ── Start / Restart ─────────────────────────────────────────────────────────

  const startGame = useCallback(() => {
    stateRef.current = initState();
    setScore(0);
    setLevel(1);
    setLives(MAX_LIVES);
    setUiState("playing");
    rafRef.current = requestAnimationFrame(loop);
  }, [initState, loop]);

  // ── Keyboard / touch controls ───────────────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        if (uiState === "idle" || uiState === "dead") { startGame(); return; }
        if (uiState === "playing") keyHeldRef.current = true;
      }
    };
    const onKeyUp = (e) => {
      if (e.code === "Space" || e.key === " ") keyHeldRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup",   onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup",   onKeyUp);
    };
  }, [uiState, startGame]);

  // Touch
  const onTouchStart = useCallback((e) => {
    e.preventDefault();
    if (uiState === "idle" || uiState === "dead") { startGame(); return; }
    keyHeldRef.current = true;
  }, [uiState, startGame]);

  const onTouchEnd = useCallback(() => { keyHeldRef.current = false; }, []);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────

  useEffect(() => {
    injectFonts();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  const heartIcons = Array.from({ length: MAX_LIVES }, (_, i) =>
    i < lives ? "❤️" : "🖤"
  );

  return (
    <Section>
      {/* ── Google Font preconnect (best-effort) */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      <GameHeader>
        <GameTitle>
          Prana <span>Runner</span>
        </GameTitle>
        <GameSubtitle>
          Ride the breath of life · collect lotus · transcend obstacles
        </GameSubtitle>
      </GameHeader>

      {/* ── HUD ─────────────────────────────────────────────────────────── */}
      <HudRow>
        <HudPill>
          <span className="icon">🏵</span>
          <span className="label">Score</span>
          <span className="value" key={scoreKey}>{score}</span>
        </HudPill>
        <HudPill>
          <span className="icon">🌟</span>
          <span className="label">Best</span>
          <span className="value">{hiScore}</span>
        </HudPill>
        <HudPill>
          <span className="icon">🔱</span>
          <span className="label">Level</span>
          <span className="value">{level}</span>
        </HudPill>
      </HudRow>

      {/* ── Lives ───────────────────────────────────────────────────────── */}
      <LivesRow>
        {heartIcons.map((h, i) => (
          <span key={i} style={{ filter: i < lives ? "none" : "grayscale(1)" }}>
            {h}
          </span>
        ))}
      </LivesRow>

      {/* ── Canvas ──────────────────────────────────────────────────────── */}
      <CanvasWrapper
        $crashed={crashed}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseDown={() => { if (uiState === "playing") keyHeldRef.current = true; }}
        onMouseUp={() => { keyHeldRef.current = false; }}
      >
        <canvas ref={canvasRef} width={W} height={H} />

        {/* Idle overlay */}
        {uiState === "idle" && (
          <OverlayBox>
            <OverlayEmoji>🧘</OverlayEmoji>
            <OverlayTitle>Prana Runner</OverlayTitle>
            <OverlayBody>
              Hold <strong>SPACE</strong> to inhale and rise · Release to sink with gravity.
              Collect lotuses 🪷 for points & healing. Avoid stress blocks ⚡.
            </OverlayBody>
            <StartButton onClick={startGame}>Begin Journey ✦</StartButton>
          </OverlayBox>
        )}

        {/* Game-over overlay */}
        {uiState === "dead" && (
          <OverlayBox>
            <OverlayEmoji>🌸</OverlayEmoji>
            <OverlayTitle>Return to Source</OverlayTitle>
            <OverlayBody>
              Score: <strong>{score}</strong> &nbsp;·&nbsp; Best: <strong>{hiScore}</strong>
              <br />
              The breath continues. Rise again.
            </OverlayBody>
            <StartButton onClick={startGame}>Breathe Again ✦</StartButton>
          </OverlayBox>
        )}
      </CanvasWrapper>

      {/* ── Controls hint ───────────────────────────────────────────────── */}
      <ControlsHint>
        <KeyBadge>SPACE</KeyBadge>
        <HintText>Hold to rise · Release to fall</HintText>
        <HintText>·</HintText>
        <HintText>🪷 +10 pts</HintText>
        <HintText>·</HintText>
        <HintText>⚡ Lose a life</HintText>
      </ControlsHint>

      {/* ── Info strip ──────────────────────────────────────────────────── */}
      <InfoStrip>
        <InfoChip>🌬 <strong>Pranayama mechanic:</strong> inhale lifts, exhale lowers</InfoChip>
        <InfoChip>🏵 <strong>Lotus collectibles</strong> restore vitality</InfoChip>
        <InfoChip>🔱 <strong>Speed rises</strong> every {LEVEL_EVERY} points</InfoChip>
      </InfoStrip>
    </Section>
  );
};

export default YogaGame;
